import { Chat } from 'dragonmouse-shared/interfaces';
import {
	resolveOptimisticallyUpdatedChats,
	setSceneLoadError,
	updateChats,
} from '../../redux/db/actions';
import store from '../../redux/store';
import { VITE_FORGE_URL } from '../../constants';
import { ChatRow } from '../types';
import { parsePostgresDate } from '../util';
import { v4 as uuid } from 'uuid';
import { Stream } from './Stream';
import { ChangeMessage, FetchError, isChangeMessage, Message, Row } from '@electric-sql/client';

export class ActiveSceneChatsStream extends Stream<ChatRow> {
	constructor(activeSceneId: string) {
		const url = VITE_FORGE_URL + `/electric/scene/${activeSceneId}/chats`;
		const params = {
			// By default, Electric sends the original shape + all its updates to us.
			// For something like chat, this is problematic -- the list gets quite
			// long as updates accumulate (which is frequent - we stream!) and we incur overhead.
			// Electric does have an experimental compaction feature but it's not
			// available in our current version.
			//
			// We can avoid this overhead by forces electric to create a new shape by
			// passing a shapeBust. Since the params are new, electric computes
			// a base shape and then pushes updates onto that. Thus we get compaction.
			//
			// Of course, this shapeBust has to fire each time we want fresh data.
			// I'm wondering actually -- if a chat session is open for a long time --
			// if we should reconstruct the ActiveSceneChatsStream periodically to
			// perform this compaction.
			//
			// Currently, we're not noticing issues for long-open chat sessions, so
			// the main purpose of this shapeBust is to prevent handle mismatch errors.
			shapeBust: uuid(),
		};

		const onError = (error: Error) => {
			if (error instanceof FetchError && error.status === 403) {
				// When the server says these scene-id-dependent streams are denied, that
				// could mean the scene has been deleted or the user has been removed from it.
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(updateChats([]));
			}
			console.error(error);
		};
		super(url, params, onError, 'activeScene.chats');
	}

	onShapeData(rows: ChatRow[]) {
		const chats: Chat[] = rows.map((chatRow) => ({
			id: chatRow.id,
			sceneId: chatRow.scene_id,
			title: chatRow.title,
			x: chatRow.x,
			y: chatRow.y,
			entries: chatRow.entries,
			metadata: chatRow.metadata,
			createdAt: parsePostgresDate(chatRow.created_at),
			updatedAt: parsePostgresDate(chatRow.updated_at),
			writeId: chatRow.write_id,
		}));
		store.dispatch(updateChats(chats));
	}

	onStreamMessages(messages: Array<unknown>) {
		const writeIds = messages
			.filter((msg): msg is ChangeMessage<ChatRow> => isChangeMessage(msg as Message<Row<never>>))
			.map((message): string => {
				return message.value.write_id || `delete:${message.value.id}`;
			});
		store.dispatch(resolveOptimisticallyUpdatedChats(writeIds));
	}
}
